import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { debounce } from 'lodash';
import { usePrevious } from 'hooks';
import { Autocomplete } from 'components/FormFields';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { truncateKeywordSpaces } from 'utils/utils';

const MultipleVehicleGroupsSelect = ({
  label,
  variant,
  field: { name, value },
  customParams,
  asyncErrors,
  margin,
  classes,
  disabled,
  id,
  form: { errors, touched, setFieldValue },
  getOptionLabel = (option) => option?.name,
}) => {
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    ...customParams,
  });
  const type = 'options';
  const options = useSelector((state) => state.vehiclesGroups[type] || []);
  const loading = useSelector((state) => state.vehiclesGroups[`${type}Loading`]);
  const prevParams = usePrevious(customParams);

  const handleShowError = () => {
    if (asyncErrors) {
      return asyncErrors;
    } else if (errors && errors[name] && touched[name]) {
      return errors[name];
    }
  };

  const handleError = () => {
    if (asyncErrors) {
      return true;
    } else if (errors && errors[name] && touched && touched[name]) {
      return true;
    }
  };

  const fetchVehiclesGroups = useCallback(
    debounce((params) => {
      const newParams = { ...params };
      if (!newParams.keyword) delete newParams.keyword;

      setParams(newParams);
      Object.keys(newParams).forEach((key) => !newParams[key] && delete newParams[key]);

      if (newParams?.keyword) {
        newParams.keyword = truncateKeywordSpaces(newParams.keyword);
      }

      dispatch(VehiclesGroupsActions.fetchVehicleGroups(newParams, type));
    }, 1000),
    []
  );

  useEffect(() => {
    if (prevParams && JSON.stringify(customParams) !== JSON.stringify(prevParams) && !disabled) {
      const newParams = {
        ...params,
        ...customParams,
      };
      setParams(newParams);
      fetchVehiclesGroups(newParams);
    }
  }, [customParams, disabled, fetchVehiclesGroups, params, prevParams]);

  const handleFetchVehiclesGroups = () => {
    if (options.length === 0 && !loading && !params?.keyword)
      dispatch(VehiclesGroupsActions.fetchVehicleGroups(params, type));
  };

  return (
    <Autocomplete
      multiple
      id={id}
      label={label}
      margin={margin}
      fullwidth="true"
      variant={variant}
      classes={classes}
      disabled={disabled}
      onHighlightChange={handleFetchVehiclesGroups}
      ChipProps={{
        color: 'primary',
      }}
      clearOnBlur={false}
      value={value}
      error={handleError()}
      helperText={handleShowError()}
      getOptionLabel={getOptionLabel}
      loading={loading}
      options={options}
      onInputChange={(_, val) => fetchVehiclesGroups({ ...params, keyword: val || '' })}
      onChange={(_, val) => {
        setFieldValue(name, val);
      }}
      getOptionSelected={(option, value) => option?.id === value?.id}
    />
  );
};

MultipleVehicleGroupsSelect.propTypes = {
  label: PropTypes.string,
  variant: PropTypes.any,
  field: PropTypes.object,
  customParams: PropTypes.any,
  asyncErrors: PropTypes.bool,
  margin: PropTypes.any,
  classes: PropTypes.any,
  disabled: PropTypes.bool,
  id: PropTypes.number,
  form: PropTypes.object,
  getOptionLabel: PropTypes.func,
};

export { MultipleVehicleGroupsSelect };
